import { apiService } from './api'
import { 
  Loan, 
  LoanFormData, 
  LoanPayment, 
  LoanSummary, 
  LoanScheduleItem, 
  LoanType, 
  EMICalculation,
  PaginatedResponse 
} from '../types'

export class LoanService {
  async getLoans(
    page = 1,
    limit = 50,
    filters?: {
      status?: 'active' | 'completed' | 'closed'
      loan_type?: string
      search?: string
    }
  ): Promise<PaginatedResponse<Loan>> {
    const params = { page, limit, ...filters }
    const response = await apiService.get<PaginatedResponse<Loan>>('/loans', params)
    return response
  }

  async getLoan(id: number): Promise<Loan> {
    const response = await apiService.get<Loan>(`/loans/${id}`)
    return response
  }

  async createLoan(data: LoanFormData): Promise<Loan> {
    // Convert camelCase to snake_case for backend API
    const backendData = {
      name: data.name,
      principal_amount: data.principalAmount,
      interest_rate: data.interestRate,
      tenure_months: data.tenureMonths,
      start_date: data.startDate,
      loan_type: data.loanType,
      ...(data.createReminder !== undefined && { create_reminder: data.createReminder })
    }
    
    const response = await apiService.post<Loan>('/loans', backendData)
    return response
  }

  async updateLoan(id: number, data: Partial<LoanFormData>): Promise<Loan> {
    // Convert camelCase to snake_case for backend API
    const backendData: any = {}
    
    if (data.name !== undefined) backendData.name = data.name
    if (data.principalAmount !== undefined) backendData.principal_amount = data.principalAmount
    if (data.interestRate !== undefined) backendData.interest_rate = data.interestRate
    if (data.tenureMonths !== undefined) backendData.tenure_months = data.tenureMonths
    if (data.startDate !== undefined) backendData.start_date = data.startDate
    if (data.loanType !== undefined) backendData.loan_type = data.loanType
    if (data.createReminder !== undefined) backendData.create_reminder = data.createReminder
    
    const response = await apiService.put<Loan>(`/loans/${id}`, backendData)
    return response
  }

  async deleteLoan(id: number): Promise<void> {
    return apiService.delete<void>(`/loans/${id}`)
  }

  async recordPayment(
    loanId: number,
    data: {
      amount: number
      paymentDate: string
      notes?: string
    }
  ): Promise<LoanPayment> {
    // Convert camelCase to snake_case for backend API
    const backendData = {
      amount: data.amount,
      payment_date: data.paymentDate,
      ...(data.notes && { notes: data.notes })
    }
    
    const response = await apiService.post<LoanPayment>(`/loans/${loanId}/payment`, backendData)
    return response
  }

  async getPayments(
    loanId: number,
    page = 1,
    limit = 50
  ): Promise<PaginatedResponse<LoanPayment>> {
    const params = { page, limit }
    const response = await apiService.get<PaginatedResponse<LoanPayment>>(`/loans/${loanId}/payments`, params)
    return response
  }

  async getLoanSchedule(loanId: number): Promise<{
    loanId: number
    schedule: LoanScheduleItem[]
  }> {
    const response = await apiService.get<{
      loanId: number
      schedule: LoanScheduleItem[]
    }>(`/loans/${loanId}/schedule`)
    return response
  }

  async getLoanSummary(loanId: number): Promise<LoanSummary> {
    const response = await apiService.get<LoanSummary>(`/loans/${loanId}/summary`)
    return response
  }

  async getDashboard(): Promise<{
    summary: {
      totalLoans: number
      activeLoans: number
      completedLoans: number
      totalPrincipal: number
      totalPaid: number
      totalRemainingAmount: number
      totalMonthlyEMI: number
    }
    activeLoans: Loan[]
    recentPayments: LoanPayment[]
  }> {
    const response = await apiService.get<any>('/loans/dashboard')
    return response
  }

  async calculateEMI(data: {
    principal_amount: number
    interest_rate: number
    tenure_months: number
  }): Promise<EMICalculation> {
    const response = await apiService.post<EMICalculation>('/loans/calculate-emi', data)
    return response
  }

  async getLoanTypes(): Promise<LoanType[]> {
    const response = await apiService.get<LoanType[]>('/loans/types')
    return response
  }

  async bulkCreateLoans(loans: LoanFormData[]): Promise<{
    created: Loan[]
    errors: Array<{ index: number; error: string; data: LoanFormData }>
    successCount: number
    errorCount: number
  }> {
    // Convert all loans to backend format
    const backendLoans = loans.map(data => ({
      name: data.name,
      principal_amount: data.principalAmount,
      interest_rate: data.interestRate,
      tenure_months: data.tenureMonths,
      start_date: data.startDate,
      loan_type: data.loanType,
      ...(data.createReminder !== undefined && { create_reminder: data.createReminder })
    }))
    
    const response = await apiService.post<any>('/loans/bulk', { loans: backendLoans })
    return response
  }
}

export const loanService = new LoanService()